import React, { Suspense, useRef, useState, useCallback, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrthographicCamera } from '@react-three/drei';
import { EffectComposer, Bloom, Vignette } from '@react-three/postprocessing';
import * as THREE from 'three';
import TetrisBlock3D from './TetrisBlock3D';
import TetrisPiece3D from './TetrisPiece3D';
import BoardGrid3D from './BoardGrid3D';
import InstancedBoard from './InstancedBoard';
import { EffectManager } from './effects';
import { SPECTRUM_COLORS, TETROMINO_COLORS, getBlockColor, getEmissiveColor } from './materials/SpectrumMaterial';
import { getTheme } from './themes';

const BOARD_ROWS = 20;
const BOARD_COLUMNS = 10;

// Camera with shake effect
const ShakeableCamera = ({ shake }) => {
  const cameraRef = useRef();
  const shakeIntensity = useRef(0);
  const shakeDecay = 0.9;

  useFrame(() => {
    if (!cameraRef.current) return;

    if (shakeIntensity.current > 0.01) {
      const offsetX = (Math.random() - 0.5) * shakeIntensity.current;
      const offsetY = (Math.random() - 0.5) * shakeIntensity.current;
      cameraRef.current.position.x = offsetX;
      cameraRef.current.position.y = 10 + offsetY;
      shakeIntensity.current *= shakeDecay;
    } else {
      cameraRef.current.position.x = 0;
      cameraRef.current.position.y = 10;
    }
  });

  // Trigger shake from external
  React.useEffect(() => {
    if (shake > 0) {
      shakeIntensity.current = shake;
    }
  }, [shake]);

  // Canvas is 200x400px, board is 10x20 units
  // 200px / 10 units = 20 pixels per unit (matches 2D cell size of 20px)
  return (
    <OrthographicCamera
      ref={cameraRef}
      makeDefault
      position={[0, 10, 20]}
      zoom={20}
      near={0.1}
      far={100}
    />
  );
};

// Scene content component
const BoardScene = ({
  boardMatrix,
  boardVersion,
  currentPiece,
  hardDropPreview,
  isLocalPlayer,
  onlineGame,
  shakeAmount,
  skinName
}) => {
  // Get theme based on skinName
  const theme = useMemo(() => getTheme(skinName), [skinName]);
  const themeConfig = theme.config || {};
  const themeColors = theme.COLORS || SPECTRUM_COLORS;

  // Get themed components
  const ThemedBlock3D = theme.Block3D || TetrisBlock3D;
  const ThemedBoardGrid3D = theme.BoardGrid3D || BoardGrid3D;
  const ThemedInstancedBoard = theme.InstancedBoard || InstancedBoard;

  // Get theme color functions for instanced rendering
  const themeColorFunctions = useMemo(() => ({
    getBlockColor: theme.getBlockColor || getBlockColor,
    getEmissiveColor: theme.getEmissiveColor || getEmissiveColor,
    colors: theme.TETROMINO_COLORS || TETROMINO_COLORS
  }), [theme]);

  // Theme-specific accent color for lights
  const accentColor = themeColors.primary || SPECTRUM_COLORS.primary;
  const secondaryAccent = themeColors.accent || '#00ffff';

  return (
    <>
      {/* Camera with shake support */}
      <ShakeableCamera shake={shakeAmount} />

      {/* Lighting - same for all players */}
      <ambientLight intensity={0.8} />
      <directionalLight
        position={[0, 20, 10]}
        intensity={1.2}
        castShadow
        shadow-mapSize-width={512}
        shadow-mapSize-height={512}
        shadow-camera-near={1}
        shadow-camera-far={50}
        shadow-camera-left={-8}
        shadow-camera-right={8}
        shadow-camera-top={25}
        shadow-camera-bottom={-5}
      />
      {/* Additional lights for all players */}
      <pointLight
        position={[0, 10, 8]}
        intensity={0.8}
        color="#ffffff"
      />
      <pointLight
        position={[-6, 15, 5]}
        intensity={0.4}
        color={accentColor}
      />
      <pointLight
        position={[6, 5, 5]}
        intensity={0.4}
        color={secondaryAccent}
      />

      {/* Board grid and borders - themed */}
      <ThemedBoardGrid3D
        rows={BOARD_ROWS}
        columns={BOARD_COLUMNS}
        currentPiece={currentPiece}
      />

      {/* Placed blocks - GPU instanced rendering for performance */}
      <ThemedInstancedBoard
        boardMatrix={boardMatrix}
        boardVersion={boardVersion}
        getBlockColorFn={themeColorFunctions.getBlockColor}
        getEmissiveColorFn={themeColorFunctions.getEmissiveColor}
        themeColors={themeColorFunctions.colors}
      />

      {/* Hard drop preview (ghost piece) - themed */}
      {hardDropPreview && (
        <TetrisPiece3D
          piece={hardDropPreview}
          isGhost={true}
          boardRows={BOARD_ROWS}
          boardColumns={BOARD_COLUMNS}
          BlockComponent={ThemedBlock3D}
        />
      )}

      {/* Current falling piece - themed */}
      {currentPiece && (
        <TetrisPiece3D
          piece={currentPiece}
          isGhost={false}
          boardRows={BOARD_ROWS}
          boardColumns={BOARD_COLUMNS}
          BlockComponent={ThemedBlock3D}
        />
      )}

      {/* Effect system - themed */}
      <EffectManager
        onlineGame={onlineGame}
        isLocalPlayer={isLocalPlayer}
        skinName={skinName}
      />

      {/* Post-processing effects - same for all players */}
      <EffectComposer>
        <Bloom
          intensity={themeConfig.bloomIntensity || 0.6}
          luminanceThreshold={themeConfig.bloomThreshold || 0.15}
          luminanceSmoothing={0.9}
          mipmapBlur
        />
        <Vignette
          offset={themeConfig.vignetteOffset || 0.3}
          darkness={themeConfig.vignetteDarkness || 0.4}
        />
      </EffectComposer>
    </>
  );
};

// Loading fallback
const LoadingFallback = () => (
  <mesh position={[0, 10, 0]}>
    <boxGeometry args={[2, 2, 2]} />
    <meshBasicMaterial color={SPECTRUM_COLORS.primary} wireframe />
  </mesh>
);

// Main component - fills parent container (parent container should set width/height)
const TetrisBoard3D = ({
  boardMatrix,
  boardVersion = 0,
  currentPiece,
  hardDropPreview,
  isLocalPlayer = true,
  onlineGame = null,
  skinName = 'Prime'
}) => {
  const [shakeAmount, setShakeAmount] = useState(0);

  // Get theme config for background color
  const theme = useMemo(() => getTheme(skinName), [skinName]);
  const backgroundColor = theme.config?.background || '#0c0c0c';

  const handleShake = useCallback(() => {
    setShakeAmount(1.5);
    // Reset shake trigger after a frame
    setTimeout(() => setShakeAmount(0), 50);
  }, []);

  // Canvas fills parent - parent container sets dimensions
  return (
    <Canvas
      style={{ width: '100%', height: '100%', display: 'block' }}
      gl={{
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance',
        toneMapping: THREE.ACESFilmicToneMapping,
        toneMappingExposure: 1.2
      }}
      shadows='basic'
      dpr={Math.min(window.devicePixelRatio, 1.5)}
      frameloop='always'
    >
      <color attach="background" args={[backgroundColor]} />
      <Suspense fallback={<LoadingFallback />}>
        <BoardScene
          boardMatrix={boardMatrix}
          boardVersion={boardVersion}
          currentPiece={currentPiece}
          hardDropPreview={hardDropPreview}
          isLocalPlayer={isLocalPlayer}
          onlineGame={onlineGame}
          shakeAmount={shakeAmount}
          onShake={handleShake}
          skinName={skinName}
        />
      </Suspense>
    </Canvas>
  );
};

export default TetrisBoard3D;
